// Reach Tool UI vocabulary: the palette map, platform list, and the small
// presentational components every page shares.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { T as O } from '../../../shared/styles/tokens.js';
import { dateToKey, friendlyRangeDate, keyToDate } from '../format.js';

// Key names kept from the module's original palette so the ~150 call sites below
// read unchanged; the values now come from the Orbit tokens. `accent` was teal
// doing double duty as both the active state and the primary fill — it is ink
// here, and active states use brass via the shared classes.
export const T = {
  bg:       O.paper,
  card:     O.surface,
  border:   O.rule,
  text:     O.text,
  muted:    O.text3,
  accent:   O.ink,
  accentBg: O.brassWash,
  green:    O.valid,
};


export function LoadingState({ title, detail = 'This may take a few seconds.', rows = 3, style }) {
  return (
    <div className="reach-async-card" role="status" aria-live="polite" aria-busy="true" style={style}>
      <div className="reach-async-head">
        <span className="reach-spinner" aria-hidden="true" />
        <div>
          <div className="o-strong">{title}</div>
          {detail && <div className="o-note" style={{ marginTop: 2 }}>{detail}</div>}
        </div>
      </div>
      {rows > 0 && <div className="reach-skeletons" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => <span className="reach-skeleton" key={index} />)}
      </div>}
    </div>
  );
}


export function ButtonSpinner() {
  return <span className="reach-spinner reach-spinner-sm" aria-hidden="true" />;
}


export function ActionMenu({ label = 'More actions', children }) {
  return (
    <details className="reach-action-menu" onClick={event => {
      const menu = event.currentTarget;
      if (event.target.closest('button')) requestAnimationFrame(() => menu.removeAttribute('open'));
    }}>
      <summary className="o-btn o-btn-ghost o-btn-sm" aria-label={label} title={label}>•••</summary>
      <div className="reach-action-menu-popover">{children}</div>
    </details>
  );
}


export const CADENCES_SCHEDULE = [
  { id: 'daily',   label: 'Daily' },
  { id: 'weekly',  label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
];


export const EXPORT_TIMEZONES = [
  { id: 'Asia/Beirut', label: 'Beirut (GMT+3)' },
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/London', label: 'London' },
  { id: 'America/New_York', label: 'New York' },
];


export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];


export function PlatformIcon({ id }) {
  const s = { width: 28, height: 28 };
  if (id === 'facebook') return (
    <svg {...s} viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="16" fill="#1877F2"/>
      <path d="M21 16.4h-3.1V27h-4.4V16.4H11.2v-3.8h2.3v-2.3c0-2.9 1.4-4.9 4.9-4.9h3v3.8h-1.9c-1.1 0-1.4.5-1.4 1.4v2h3.4z" fill="#fff"/>
    </svg>
  );
  if (id === 'snapchat') return (
    <svg {...s} viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="16" fill="#FFFC00"/>
      <path d="M16 8c2.6 0 4.6 2.2 4.6 5v1.6c0 .5.5.8 1 .6l.6-.2c.4-.1.8.2.7.6-.1.6-.6 1.1-1.6 1.5.2.5.7 1 1.6 1.3.3.1.4.5.1.7-.4.3-1 .5-1.5.7 0 .3-.1.7-.2 1-.9-.1-1.6.1-2.3.6-.7.5-1.4 1.1-2.9 1.1s-2.3-.6-2.9-1.1c-.7-.5-1.4-.7-2.3-.6-.1-.3-.2-.7-.2-1-.6-.2-1.1-.4-1.5-.7-.3-.2-.2-.6.1-.7.9-.3 1.4-.8 1.6-1.3-1-.4-1.5-.9-1.6-1.5-.1-.4.3-.7.7-.6l.6.2c.5.2 1-.1 1-.6V13c0-2.8 2-5 4.6-5z" fill="#000"/>
    </svg>
  );
  if (id === 'tiktok') return (
    <svg {...s} viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="16" fill="#000"/>
      <path d="M18.6 8h2.3c.1 1.3.9 2.7 2.5 3.3v2.4c-1 0-2-.3-2.9-.8v5.4c0 2.7-2.2 4.7-4.7 4.7-2.6 0-4.7-2.1-4.7-4.7 0-2.6 2.1-4.7 4.7-4.7.3 0 .5 0 .8.1v2.4c-.2-.1-.5-.1-.8-.1-1.2 0-2.2 1-2.2 2.2 0 1.3 1 2.3 2.2 2.3s2.3-1 2.3-2.3V8z" fill="#fff"/>
    </svg>
  );
  if (id === 'linkedin') return (
    <svg {...s} viewBox="0 0 32 32">
      <rect width="32" height="32" rx="4" fill="#0A66C2"/>
      <path d="M9.4 12.6h3.3V23H9.4V12.6zM11 11c-1.1 0-1.8-.7-1.8-1.6S9.9 7.8 11 7.8s1.8.7 1.8 1.6S12.1 11 11 11zM14.6 12.6h3.2v1.4h.1c.4-.8 1.5-1.7 3.1-1.7 3.3 0 3.9 2.2 3.9 5V23h-3.3v-4.8c0-1.1 0-2.6-1.6-2.6s-1.9 1.3-1.9 2.5V23h-3.3V12.6z" fill="#fff"/>
    </svg>
  );
  return null;
}


export const PLATFORMS = [
  { id: 'facebook',  label: 'Facebook' },
  { id: 'snapchat',  label: 'Snapchat' },
  { id: 'tiktok',    label: 'TikTok' },
  { id: 'linkedin',  label: 'LinkedIn' },
];

// Fake pools used to auto-fill accounts/ad accounts until the Account/Ad accounts
// steps are wired to a real data source.

// Connection emails, the accounts each can see, and their display names all come from the
// platform now (useConnections / useAdAccounts). The mock catalogs that used to stand in for
// them are gone deliberately rather than left dormant: a stale hardcoded account list is
// exactly the thing someone later reads to answer "which accounts do we have?".


export function defaultStreamName(platform, cadence) {
  const platformLabel = PLATFORMS.find(p => p.id === platform)?.label || platform;
  const cadenceLabel  = CADENCES_SCHEDULE.find(c => c.id === cadence)?.label || cadence;
  return `${platformLabel} ${cadenceLabel} Reach`;
}


export const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});


export function TimeSelect({ value, onChange, style }) {
  return (
    <select aria-label="Run time"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '10px 12px', borderRadius: 8,
        border: `1px solid ${T.border}`, background: T.card, color: T.text,
        fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
        ...style,
      }}
    >
      <option value="" disabled>Select time</option>
      {TIME_OPTIONS.map(t => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
  );
}


export function OptionCard({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        flex: 1,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '18px 20px',
        borderRadius: 12,
        border: `1.5px solid ${active ? T.accent : T.border}`,
        background: active ? T.accentBg : T.card,
        color: T.text,
        fontFamily: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {children}
    </button>
  );
}


export function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding:      '8px 16px',
        borderRadius: 8,
        border:       `1px solid ${active ? T.accent : T.border}`,
        background:   active ? T.accentBg : T.card,
        color:        active ? T.accent : T.muted,
        fontSize:     13,
        fontWeight:   600,
        cursor:       'pointer',
        transition:   'border-color 0.15s, color 0.15s',
      }}
    >
      {children}
    </button>
  );
}


export function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.muted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      {children}
    </div>
  );
}


export function Radio({ active }) {
  return (
    <span style={{
      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
      border: `2px solid ${active ? T.accent : T.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {active && <span style={{ width: 9, height: 9, borderRadius: '50%', background: T.accent }} />}
    </span>
  );
}


export const STATUS_COLORS = {
  standby:    O.rule,
  processing: O.brassLift,
  successful: O.valid,
  failed:     O.error,
};


export function ControlCard({ children, style }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: '18px 20px', display: 'flex', flexDirection: 'column', ...style,
    }}>
      {children}
    </div>
  );
}


export function LegendDot({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.muted }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}


export function SegmentedBar({ segments, height = 8 }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  return (
    <div style={{ display: 'flex', width: '100%', height, borderRadius: height / 2, overflow: 'hidden', background: O.ruleSoft }}>
      {segments.filter(s => s.value > 0).map((s, i) => (
        <div key={i} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
      ))}
    </div>
  );
}


export function StatusToggle({ on, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      role="switch"
      aria-checked={on}
      aria-label="Stream enabled"
      onClick={(e) => { e.stopPropagation(); onChange(!on); }}
      style={{
        width: 36, height: 20, borderRadius: 20, border: 'none', cursor: 'pointer',
        background: on ? T.accent : T.border,
        position: 'relative', flexShrink: 0, padding: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: on ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: O.surface,
        transition: 'left 0.15s',
      }} />
    </button>
  );
}


// bp_queue has more states than the original mock did. Queued, cancelled and never-run are
// muted rather than red: none of them is a fault, and colouring "waiting its turn" the same as
// "failed" would send someone looking for a problem that is not there.
export const RUN_STATUS_TONE = {
  Successful: () => T.green,
  Processing: () => O.brassLift,
  Queued: () => T.muted,
  // A deliberate hold, not a fault — red would send someone hunting for a problem they
  // caused on purpose.
  Paused: () => T.muted,
  Cancelled: () => T.muted,
  'Never run': () => T.muted,
};


export function RunStatusBadge({ status }) {
  const color = (RUN_STATUS_TONE[status] || (() => O.error))();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color, fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {status}
    </span>
  );
}


export function ToolbarButton({ label, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '7px 14px', borderRadius: 8, border: `1px solid ${T.border}`,
        background: 'none', color: disabled ? O.text3 : T.text,
        fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
}


export function BackButton({ onClick, label = 'Back to Data Streams' }) {
  return (
    <button
      type="button"
      data-reach-leave
      onClick={onClick}
      style={{
        padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.border}`,
        background: 'none', color: T.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 20,
      }}
    >
      ← {label}
    </button>
  );
}


export function Modal({ onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(4, 8, 16, 0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12,
          padding: '28px 28px 24px', maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto',
          position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute', top: 14, right: 16, background: 'none', border: 'none',
            color: T.muted, fontSize: 20, lineHeight: 1, cursor: 'pointer',
          }}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}


export const RANGE_PRESETS = [
  { label: 'Yesterday', getRange: today => { const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1); return [yesterday, yesterday]; } },
  { label: 'Last 7 days', getRange: today => [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6), today] },
  { label: 'Last 14 days', getRange: today => [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13), today] },
  { label: 'Last 30 days', getRange: today => [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29), today] },
  { label: 'Last 90 days', getRange: today => [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 89), today] },
  { label: 'Last 365 days', getRange: today => [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 364), today] },
  { label: 'Last week', getRange: today => {
    const mondayOffset = (today.getDay() + 6) % 7;
    const thisMonday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - mondayOffset);
    return [new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 7), new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 1)];
  } },
  { label: 'Week to date', getRange: today => [new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7)), today] },
  { label: 'Last month', getRange: today => [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)] },
  { label: 'Month to date', getRange: today => [new Date(today.getFullYear(), today.getMonth(), 1), today] },
  { label: 'Last quarter', getRange: today => { const quarterStart = Math.floor(today.getMonth() / 3) * 3; return [new Date(today.getFullYear(), quarterStart - 3, 1), new Date(today.getFullYear(), quarterStart, 0)]; } },
  { label: 'Quarter to date', getRange: today => [new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1), today] },
  { label: 'Last year', getRange: today => [new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, 11, 31)] },
  { label: 'Last year to date', getRange: today => [new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())] },
  { label: 'Year to date', getRange: today => [new Date(today.getFullYear(), 0, 1), today] },
];


export function DateRangePicker({ label = 'Date range', value, onChange, min = '', max = '', ongoingMax = '', disabled = false, onClear = null, emptyLabel = 'Select start and end date', modal = false, allowOngoing = true }) {
  const [open, setOpen] = useState(false);
  const [calendarView, setCalendarView] = useState('days');
  const initialDate = keyToDate(value?.from || value?.to || max) || new Date();
  const [visibleMonth, setVisibleMonth] = useState(() => new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const today = new Date();
  const firstDayOffset = (new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1).getDay() + 6) % 7;
  const daysInMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0).getDate();
  const calendarDays = [...Array(firstDayOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, index) => index + 1)];
  const ongoingTo = ongoingMax || max || dateToKey(new Date());
  const effectiveTo = value?.ongoing ? ongoingTo : value?.to;
  useEffect(() => {
    if (value?.ongoing && value.to !== ongoingTo) onChange({ ...value, to: ongoingTo });
  }, [value?.ongoing, value?.to, ongoingTo]);
  const clampKey = key => min && key < min ? min : max && key > max ? max : key;
  const chooseDate = day => {
    const key = dateToKey(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day));
    if (!value?.from || effectiveTo) onChange({ from: key, to: '', ongoing: false });
    else if (key < value.from) onChange({ from: key, to: value.from, ongoing: false });
    else onChange({ from: value.from, to: key, ongoing: false });
  };
  const applyPreset = preset => {
    const [fromDate, toDate] = preset.getRange(today);
    const from = clampKey(dateToKey(fromDate));
    const to = clampKey(dateToKey(toDate));
    onChange(from <= to ? { from, to, ongoing: false } : { from: to, to, ongoing: false });
    setVisibleMonth(new Date(keyToDate(to).getFullYear(), keyToDate(to).getMonth(), 1));
    setOpen(false);
  };
  const navigateCalendar = offset => setVisibleMonth(current => calendarView === 'days'
    ? new Date(current.getFullYear(), current.getMonth() + offset, 1)
    : calendarView === 'months'
      ? new Date(current.getFullYear() + offset, current.getMonth(), 1)
      : new Date(current.getFullYear() + (offset * 10), current.getMonth(), 1));
  const rangeLabel = value?.from || effectiveTo ? `${friendlyRangeDate(value?.from)} → ${value?.ongoing ? 'Ongoing' : friendlyRangeDate(effectiveTo)}` : emptyLabel;
  const decadeStart = Math.floor(visibleMonth.getFullYear() / 10) * 10;
  const monthOptions = Array.from({ length: 12 }, (_, month) => ({ month, label: new Date(2000, month, 1).toLocaleDateString(undefined, { month: 'short' }) }));

  return <div style={{ position: 'relative' }}>
    <button type="button" className="o-control" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} onClick={() => { setOpen(current => !current); setCalendarView('days'); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%', minWidth: 0, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 11 }}><span style={{ display: 'flex', alignItems: 'center', minWidth: 0, whiteSpace: 'nowrap' }}><span aria-hidden="true" style={{ marginRight: 6, flexShrink: 0 }}>▦</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{rangeLabel}</span></span><span aria-hidden="true" style={{ flexShrink: 0 }}>⌄</span></button>
    {open && modal && <div role="presentation" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 89, background: 'rgba(23, 26, 31, 0.42)' }} />}
    {open && <div className="o-popover" role="dialog" aria-modal={modal || undefined} aria-label={label} onKeyDown={event => { if (event.key === 'Escape') setOpen(false); }} style={modal ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 90, width: 'min(390px, calc(100vw - 32px))', maxHeight: 'min(460px, 86vh)', overflowY: 'auto', padding: 12 } : { position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 90, width: 'min(390px, calc(100vw - 48px))', maxHeight: 'min(460px, 76vh)', overflowY: 'auto', padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}><div><strong style={{ fontSize: 13 }}>{label}</strong><div className="o-note" style={{ marginTop: 2 }}>{value?.from && !value?.to ? 'Choose the end date.' : 'Quick range or two dates'}</div></div><button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label={`Close ${label}`} onClick={() => setOpen(false)} style={{ width: 28 }}>×</button></div>
      <select className="o-control" aria-label="Quick date range" defaultValue="" onChange={event => { const preset = RANGE_PRESETS.find(item => item.label === event.target.value); if (preset) applyPreset(preset); }} style={{ marginBottom: 8 }}><option value="">Quick range…</option>{RANGE_PRESETS.map(preset => {
          const [presetFrom, presetTo] = preset.getRange(today);
          const unavailable = (min && dateToKey(presetTo) < min) || (max && dateToKey(presetFrom) > max);
          return <option key={preset.label} value={preset.label} disabled={unavailable}>{preset.label}</option>;
        })}</select>
      <div><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}><button type="button" className="o-btn o-btn-sm" aria-label={calendarView === 'days' ? 'Previous month' : calendarView === 'months' ? 'Previous year' : 'Previous decade'} onClick={() => navigateCalendar(-1)} style={{ width: 28 }}>‹</button><button type="button" aria-label={calendarView === 'days' ? 'Choose month' : calendarView === 'months' ? 'Choose year' : undefined} onClick={() => setCalendarView(current => current === 'days' ? 'months' : current === 'months' ? 'years' : current)} style={{ width: 'auto', padding: '4px 8px', color: T.text, fontWeight: 700 }}>{calendarView === 'days' ? visibleMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : calendarView === 'months' ? visibleMonth.getFullYear() : `${decadeStart}–${decadeStart + 9}`}</button><button type="button" className="o-btn o-btn-sm" aria-label={calendarView === 'days' ? 'Next month' : calendarView === 'months' ? 'Next year' : 'Next decade'} onClick={() => navigateCalendar(1)} style={{ width: 28 }}>›</button></div>
        {calendarView === 'days' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(30px, 1fr))', gap: 2 }}>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <div key={day} className="o-field-lbl" style={{ textAlign: 'center', padding: '2px 0' }}>{day}</div>)}{calendarDays.map((day, index) => {
            if (!day) return <span key={`blank-${index}`} />;
            const key = dateToKey(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day));
            const unavailable = (min && key < min) || (max && key > max);
            const selected = key === value?.from || key === effectiveTo;
            const inRange = value?.from && effectiveTo && key > value.from && key < effectiveTo;
            return <button key={key} type="button" disabled={unavailable} aria-label={key} aria-pressed={selected} onClick={() => chooseDate(day)} style={{ minHeight: 28, padding: 3, borderRadius: selected ? 4 : inRange ? 2 : 4, border: selected ? `1px solid ${T.accent}` : '1px solid transparent', background: selected ? T.accent : inRange ? O.rule : 'transparent', color: selected ? O.textInvert : unavailable ? T.muted : T.text, opacity: unavailable ? 0.35 : 1, cursor: unavailable ? 'not-allowed' : 'pointer', fontWeight: selected ? 700 : 500 }}>{day}</button>;
          })}</div>}
        {calendarView === 'months' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, padding: '8px 0' }}>{monthOptions.map(({ month, label: monthLabel }) => {
          const monthStart = dateToKey(new Date(visibleMonth.getFullYear(), month, 1));
          const monthEnd = dateToKey(new Date(visibleMonth.getFullYear(), month + 1, 0));
          const unavailable = (min && monthEnd < min) || (max && monthStart > max);
          return <button key={month} type="button" disabled={unavailable} aria-label={`${monthLabel} ${visibleMonth.getFullYear()}`} aria-pressed={visibleMonth.getMonth() === month} onClick={() => { setVisibleMonth(current => new Date(current.getFullYear(), month, 1)); setCalendarView('days'); }} style={{ padding: '10px 6px', background: visibleMonth.getMonth() === month ? O.surface2 : 'transparent', color: unavailable ? T.muted : T.text }}>{monthLabel}</button>;
        })}</div>}
        {calendarView === 'years' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, padding: '8px 0' }}>{Array.from({ length: 10 }, (_, index) => decadeStart + index).map(year => {
          const yearStart = `${year}-01-01`;
          const yearEnd = `${year}-12-31`;
          const unavailable = (min && yearEnd < min) || (max && yearStart > max);
          return <button key={year} type="button" disabled={unavailable} aria-label={`Select ${year}`} aria-pressed={visibleMonth.getFullYear() === year} onClick={() => { setVisibleMonth(current => new Date(year, current.getMonth(), 1)); setCalendarView('months'); }} style={{ padding: '9px 6px', background: visibleMonth.getFullYear() === year ? O.surface2 : 'transparent', color: unavailable ? T.muted : T.text }}>{year}</button>;
        })}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}><div className="o-note"><b>{friendlyRangeDate(value?.from)}</b> → <b>{value?.ongoing ? 'Ongoing' : friendlyRangeDate(effectiveTo)}</b></div><div style={{ display: 'flex', gap: 6 }}>{(onClear || value?.from || effectiveTo) && <button type="button" className="o-btn o-btn-sm" onClick={() => { (onClear || onChange)({ from: '', to: '', ongoing: false }); setOpen(false); }} style={{ width: 'auto' }}>Clear</button>}{allowOngoing && <button type="button" className="o-btn o-btn-sm" disabled={!value?.from} aria-pressed={Boolean(value?.ongoing)} onClick={() => { onChange({ from: value.from, to: ongoingTo, ongoing: true }); setOpen(false); }} style={{ width: 'auto' }}>Ongoing</button>}<button type="button" className="o-btn o-btn-primary o-btn-sm" disabled={!value?.from || (!effectiveTo && !value?.ongoing)} onClick={() => setOpen(false)} style={{ width: 'auto' }}>Apply</button></div></div>
    </div>}
  </div>;
}


export function ColumnHeaderFilter({ label, value = '', onChange, options = null, placeholder = 'Filter values…', onLabelClick = null }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const trigger = useRef(null);
  const menu = useRef(null);
  const close = () => { setOpen(false); trigger.current?.focus(); };
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const box = trigger.current.getBoundingClientRect();
      setPosition({ left: Math.max(8, Math.min(box.left, window.innerWidth - 238)), top: Math.max(8, Math.min(box.bottom + 6, window.innerHeight - 148)) });
    };
    place();
    window.addEventListener('scroll', place, true); window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    menu.current?.querySelector('input,select')?.focus();
    const outside = event => { if (!trigger.current?.contains(event.target) && !menu.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  const active = String(value).length > 0;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {onLabelClick ? <button type="button" className="o-btn o-btn-ghost o-btn-sm" onClick={onLabelClick} style={{ border: 0, padding: 0, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}>{label}</button> : <span>{label}</span>}
      <button ref={trigger} type="button" className="o-btn o-btn-sm reach-history-filter-trigger" aria-label={`Filter ${label}`} aria-pressed={active} aria-expanded={open} onClick={event => { event.stopPropagation(); setOpen(current => !current); }}>▼</button>
      {open && createPortal(<div ref={menu} role="dialog" aria-label={`Filter ${label}`} className="o-popover reach-history-filter-menu" style={position} onClick={event => event.stopPropagation()} onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); close(); } }}>
        {options ? <select className="o-control" aria-label={`${label} column filter`} value={value} onChange={event => onChange(event.target.value)}><option value="">All values</option>{options.map(option => <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? option}</option>)}</select> : <input className="o-control" type="search" aria-label={`${label} column filter`} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} autoFocus />}
        <span className="reach-history-filter-actions"><button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={!active} onClick={() => onChange('')}>Clear</button><button type="button" className="o-btn o-btn-sm" onClick={close}>Done</button></span>
      </div>, document.body)}
    </span>
  );
}


export function ResizableHeaderCell({ children, width, onWidthChange, style = {} }) {
  const beginResize = event => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = width;
    const move = moveEvent => onWidthChange(Math.max(72, startWidth + moveEvent.clientX - startX));
    const stop = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
  };
  return <th style={{ ...style, width, minWidth: width, position: 'relative' }}>{children}<span role="separator" aria-orientation="vertical" aria-label="Resize column" tabIndex={0} aria-valuemin={72} aria-valuenow={Math.round(width)} onKeyDown={event => { if (['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); onWidthChange(Math.max(72, width + (event.key === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? 50 : 10))); } }} onMouseDown={beginResize} style={{ position: 'absolute', top: 0, right: -3, width: 7, height: '100%', cursor: 'col-resize', zIndex: 5 }}><span style={{ position: 'absolute', top: '25%', bottom: '25%', left: 3, width: 1, background: T.border }} /></span></th>;
}

